import { useState, useEffect, useCallback, useMemo } from 'react';
import { DataGridPro, GridInputRowSelectionModel, GridColDef } from '@mui/x-data-grid-pro';
import { SubMenu } from './SubMenu';
import { useTranslation } from 'react-i18next';
import { TimekeeperService } from 'services/TimekeeperService';
import { BalanceDomino } from 'repository/timekeeper/@types/BalanceDomino';

type ResponseBalanceDomino = BalanceDomino;

const BalanceDominosTable = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<BalanceDomino[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedBalanceDomino, setSelectedBalanceDomino] = useState<GridInputRowSelectionModel | undefined>([]);
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [selected, setSelected] = useState<BalanceDomino[]>([]);

    const timekeeperService = useMemo(() => new TimekeeperService(), []);

    const columns: GridColDef[] = [
        { field: 'name', headerName: t("accruals.accrual_codes.table.columns.name"), flex: 1 },
        { field: 'description', headerName: t("general.description"), flex: 1 },
        { field: 'type_accrual', headerName: t("accruals.accrual_codes.table.columns.type_accrual"), flex: 1, headerAlign: 'center', align: 'center'},
    ];

    const reloading = useCallback(() => {
        setLoading(true);
        timekeeperService.balance_domino.getAll()
            .then((data: ResponseBalanceDomino[]) => {
                setRows(data);
            })
            .catch((error) => {
                console.error('Error al obtener los balances domino:', error);
                setRows([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [timekeeperService.balance_domino]);

    useEffect(() => {
        reloading();
    }, [reloading]);

    const cleanSelected = () => {
        setSelected([]);
        setSelectedBalanceDomino([]);
    }

    return (
        <div style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null)
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                columns={columns}
                rows={rows}
                loading={loading}
                getRowId={(row) => row.balance_domino_id}
                rowSelectionModel={selectedBalanceDomino}
                pagination
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    const selectedIds = newRowSelectionModel as (string | number)[];
                    const row = rows.filter((r) => selectedIds.includes(r.balance_domino_id));
                    setSelectedBalanceDomino(newRowSelectionModel);
                    setSelected(row);
                }}
                checkboxSelection
                localeText={{
                    headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                    headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                    headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                    headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                    headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                    headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                    footerRowSelected: (count) =>
                        count > 1 ? `${count.toLocaleString()} filas seleccionadas`
                            : `${count.toLocaleString()} fila seleccionada`,
                    footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                    MuiTablePagination: {
                        labelDisplayedRows: ({ from, to, count }) =>
                            `${from} - ${to} of ${count === -1 ? `more than ${to}` : count}`,
                        labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                    },
                }}
                showCellVerticalBorder
                showColumnVerticalBorder
                disableColumnMenu
                headerFilters
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    }
                }}
            />
            <SubMenu
                selected={selected}
                cleanSelected={cleanSelected}
                rows={rows}
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                reloading={reloading}
            />
        </div>
    );
}

export { BalanceDominosTable };